var __defProp = Object.defineProperty;
var __name = (target, value) => __defProp(target, "name", { value, configurable: true });

// src/utils.ts
var isFloat = /* @__PURE__ */ __name((num) => num % 1 !== 0, "isFloat");

// src/ext/TimestampEncoder.ts
var uint64bound = 4294967296;
var timestampEncoder = {
  type: -1,
  check(input) {
    return input instanceof Date;
  },
  // almost copy-pated from msgpack5's DateCodec implementation
  // https://github.com/mcollina/msgpack5/blob/master/lib/codecs/DateCodec.js
  encode(input) {
    let millis = input.getTime();
    let seconds = millis / 1e3 | 0;
    let nanos = (millis - seconds * 1e3) * 1e6;
    let bound = uint64bound - 1;
    if (nanos || seconds > bound) {
      let array = new Uint8Array(10);
      let view = new DataView(array.buffer);
      let upperNanos = nanos * 4;
      let upperSeconds = seconds / uint64bound;
      let upper = upperNanos + upperSeconds & bound;
      let lower = seconds & bound;
      view.setUint8(0, 215);
      view.setInt8(1, -1);
      view.setUint32(2, upper, false);
      view.setUint32(6, lower, false);
      return array;
    } else {
      let array = new Uint8Array(6);
      let view = new DataView(array.buffer);
      view.setUint8(0, 214);
      view.setInt8(1, -1);
      view.setUint32(2, millis / 1e3 | 0, false);
      return array;
    }
  }
};

// src/encoder.ts
var uint16min = 256;
var uint32min = 65536;
var uint64min = 4294967296;
function makeMessagePackEncoder({
  encoderExtensions = []
} = {}) {
  let textEncoder = new TextEncoder();
  const encodeFloat = /* @__PURE__ */ __name((input, force64 = false) => {
    let is64 = force64 || Math.fround(input) !== input;
    let array = new Uint8Array(is64 ? 9 : 5);
    let view = new DataView(array.buffer);
    if (is64) {
      array[0] = 203;
      view.setFloat64(1, input, false);
    } else {
      array[0] = 202;
      view.setFloat32(1, input, false);
    }
    return array;
  }, "encodeFloat");
  const encodeInteger = /* @__PURE__ */ __name((input) => {
    if (input === 0) {
      return new Uint8Array([0]);
    } else if (input > 0) {
      if (input < 128) {
        return numberUint8Array(input, 8);
      } else if (input < uint16min) {
        return numberUint8Array(input, 8, 204);
      } else if (input < uint32min) {
        return numberUint8Array(input, 16, 205);
      } else if (input < uint64min) {
        return numberUint8Array(input, 32, 206);
      } else if (input <= Number.MAX_SAFE_INTEGER) {
        return numberUint8Array(input, 64, 207);
      } else {
        throw new Error("number is lager than 2^53, try bigint");
      }
    } else {
      if (input >= -32) {
        return numberUint8Array(uint16min + input, 8);
      } else if (input >= -uint16min / 2) {
        return numberUint8Array(input, 8, 208);
      } else if (input >= -uint32min / 2) {
        return numberUint8Array(input, 16, 209);
      } else if (input >= -uint64min / 2) {
        return numberUint8Array(input, 32, 210);
      } else if (input >= Number.MIN_SAFE_INTEGER) {
        let absArray = numberUint8Array(Math.abs(input), 64, 211);
        let i = absArray.length;
        while (i-- > 1) {
          if (absArray[i] !== 0) {
            absArray[i] = (absArray[i] ^ 255) + 1;
            break;
          }
        }
        while (i-- > 1) {
          absArray[i] = absArray[i] ^ 255;
        }
        return absArray;
      } else {
        throw new Error("number is smaller than -2^53, try bigint");
      }
    }
  }, "encodeInteger");
  const encodeBigInt = /* @__PURE__ */ __name((input) => {
    if (input === BigInt(0)) {
      return new Uint8Array([0]);
    } else if (input > 0) {
      let array = new Uint8Array(9);
      array[0] = 207;
      let view = new DataView(array.buffer, 1);
      view.setBigUint64(0, input, false);
      return array;
    } else {
      let array = new Uint8Array(9);
      array[0] = 211;
      let view = new DataView(array.buffer, 1);
      view.setBigInt64(0, input, false);
      return array;
    }
  }, "encodeBigInt");
  const encodeString = /* @__PURE__ */ __name((input) => {
    let array;
    let buffer = textEncoder.encode(input);
    let len = buffer.length;
    if (len < 32) {
      array = new Uint8Array(1 + len);
      array[0] = 160 | len;
      array.set(buffer, 1);
    } else if (len < 256) {
      array = new Uint8Array(2 + len);
      array[0] = 217;
      array[1] = len;
      array.set(buffer, 2);
    } else if (len < 65536) {
      array = new Uint8Array(3 + len);
      array[0] = 218;
      array[1] = 255 & len >> 8;
      array[2] = 255 & len;
      array.set(buffer, 3);
    } else if (len < uint64min) {
      array = new Uint8Array(5 + len);
      array[0] = 219;
      array[1] = 255 & len >> 24;
      array[2] = 255 & len >> 16;
      array[3] = 255 & len >> 8;
      array[4] = 255 & len;
      array.set(buffer, 5);
    } else {
      array = new Uint8Array();
    }
    return array;
  }, "encodeString");
  const encodeBinary = /* @__PURE__ */ __name((input) => {
    let buf;
    let len = input.length;
    if (len < 256) {
      buf = new Uint8Array(2 + len);
      buf[0] = 196;
      buf[1] = len;
      buf.set(input, 2);
    } else if (len < 65536) {
      buf = new Uint8Array(3 + len);
      buf[0] = 197;
      buf[1] = len >> 8;
      buf[2] = 255 & len;
      buf.set(input, 3);
    } else if (len < uint64min) {
      buf = new Uint8Array(5 + len);
      buf[0] = 198;
      buf[1] = 255 & len >> 24;
      buf[2] = 255 & len >> 16;
      buf[3] = 255 & len >> 8;
      buf[4] = 255 & len;
      buf.set(input, 5);
    } else {
      buf = new Uint8Array();
    }
    return buf;
  }, "encodeBinary");
  const encodeArray = /* @__PURE__ */ __name((items) => {
    let itemLen = items.length;
    let bufLen = 0;
    let cache = Array(itemLen);
    for (let i = 0; i < itemLen; i++) {
      let buf = encode(items[i]);
      cache[i] = buf;
      bufLen += buf.length;
    }
    let array;
    let offset = 0;
    if (itemLen < 16) {
      offset = 1;
      array = new Uint8Array(offset + bufLen);
      array[0] = 144 | itemLen;
    } else if (itemLen < 65536) {
      offset = 3;
      array = new Uint8Array(offset + bufLen);
      array[0] = 220;
      array[1] = 255 & itemLen >> 8;
      array[2] = 255 & itemLen;
    } else if (itemLen < 4294967296) {
      offset = 5;
      array = new Uint8Array(offset + bufLen);
      array[0] = 221;
      array[1] = 255 & itemLen >> 24;
      array[2] = 255 & itemLen >> 16;
      array[3] = 255 & itemLen >> 8;
      array[4] = 255 & itemLen;
    } else {
      throw new Error("array length is too long");
    }
    for (let subarray of cache) {
      for (let i = 0; i < subarray.length; i++) {
        array[offset++] = subarray[i];
      }
    }
    return array;
  }, "encodeArray");
  const encodeMap = /* @__PURE__ */ __name((input) => {
    let entries = Object.entries(input);
    let entriesLen = entries.length;
    let keyCache = Array(entriesLen);
    let valCache = Array(entriesLen);
    let bufLen = 0;
    for (let i = 0; i < entriesLen; i++) {
      let [key, value] = entries[i];
      let keyBuf = encodeString(key);
      keyCache[i] = keyBuf;
      let valueBuf = encode(value);
      valCache[i] = valueBuf;
      bufLen += keyBuf.length + valueBuf.length;
    }
    let array;
    let offset = 0;
    if (entriesLen < 16) {
      offset = 1;
      array = new Uint8Array(offset + bufLen);
      array[0] = 128 | entriesLen;
    } else if (entriesLen < 65536) {
      offset = 3;
      array = new Uint8Array(offset + bufLen);
      array[0] = 222;
      array[1] = 255 & entriesLen >> 8;
      array[2] = 255 & entriesLen;
    } else if (entriesLen < 4294967296) {
      offset = 5;
      array = new Uint8Array(offset + bufLen);
      array[0] = 223;
      array[1] = 255 & entriesLen >> 24;
      array[2] = 255 & entriesLen >> 16;
      array[3] = 255 & entriesLen >> 8;
      array[4] = 255 & entriesLen;
    } else {
      throw new Error("map size is too big");
    }
    for (let i = 0; i < entriesLen; i++) {
      array.set(keyCache[i], offset);
      offset += keyCache[i].length;
      array.set(valCache[i], offset);
      offset += valCache[i].length;
    }
    return array;
  }, "encodeMap");
  let encode = /* @__PURE__ */ __name((input) => {
    if (typeof input === "string") {
      return encodeString(input);
    }
    if (Array.isArray(input)) {
      return encodeArray(input);
    }
    if (input instanceof Uint8Array) {
      return encodeBinary(input);
    } else if (input === null) {
      return new Uint8Array([192]);
    } else if (typeof input === "object") {
      if (timestampEncoder.check(input)) {
        return timestampEncoder.encode(input);
      }
      for (let ext of encoderExtensions) {
        if (ext.check(input)) {
          return ext.encode(input);
        }
      }
      return encodeMap(input);
    }
    if (input === false) {
      return new Uint8Array([194]);
    }
    if (input === true) {
      return new Uint8Array([195]);
    }
    if (typeof input === "number") {
      if (isFloat(input)) {
        return encodeFloat(input);
      } else {
        return encodeInteger(input);
      }
    }
    if (typeof input === "bigint") {
      return encodeBigInt(input);
    }
    return new Uint8Array();
  }, "encode");
  return { encode };
}
__name(makeMessagePackEncoder, "makeMessagePackEncoder");
var numberUint8Array = /* @__PURE__ */ __name((input, bits, header) => {
  let array = new Uint8Array((bits >> 3) + (header ? 1 : 0));
  let cursor = 0;
  if (header) {
    array[cursor++] = header;
  }
  if (bits < 64) {
    for (let i = bits - 8; i >= 0; i -= 8) {
      array[cursor++] = 255 & input >> i;
    }
  } else {
    let lo = input % uint64min;
    let hi = input / uint64min | 0;
    for (let i = 24; i >= 0; i -= 8) {
      array[cursor++] = 255 & hi >> i;
    }
    for (let i = 24; i >= 0; i -= 8) {
      array[cursor++] = 255 & lo >> i;
    }
  }
  return array;
}, "numberUint8Array");

// ../../node_modules/@rescript/std/lib/es6/caml_option.js
function some(x) {
  if (x === void 0) {
    return {
      BS_PRIVATE_NESTED_SOME_NONE: 0
    };
  } else if (x !== null && x.BS_PRIVATE_NESTED_SOME_NONE !== void 0) {
    return {
      BS_PRIVATE_NESTED_SOME_NONE: x.BS_PRIVATE_NESTED_SOME_NONE + 1 | 0
    };
  } else {
    return x;
  }
}
__name(some, "some");

// ../../node_modules/@rescript/std/lib/es6/js_exn.js
function raiseError(str) {
  throw new Error(str);
}
__name(raiseError, "raiseError");

// ../../node_modules/@rescript/std/lib/es6/js_dict.js
function get(dict, k) {
  if (k in dict) {
    return some(dict[k]);
  }
}
__name(get, "get");
function fromArray(entries) {
  var dict = {};
  var l = entries.length;
  for (var i = 0; i < l; ++i) {
    var match = entries[i];
    dict[match[0]] = match[1];
  }
  return dict;
}
__name(fromArray, "fromArray");

// ../../node_modules/@rescript/std/lib/es6/pervasives.js
function lnot(x) {
  return x ^ -1;
}
__name(lnot, "lnot");

// src/ext/TimestampDecoder.ts
var timestampDecoder = {
  type: -1,
  // almost copy-pated from msgpack5's DateCodec implementation
  // https://github.com/mcollina/msgpack5/blob/master/lib/codecs/DateCodec.js
  decode(dataArray, byteLength) {
    let view = new DataView(dataArray.buffer);
    switch (byteLength) {
      case 4: {
        let seconds = view.getUint32(0, false);
        let millis = seconds / 1e3 | 0;
        return new Date(millis);
      }
      case 8: {
        let upper = view.getUint32(0);
        let lower = view.getUint32(4);
        let nanos = upper / 4;
        let seconds = (upper & 3) * 2 ** 32 + lower;
        let millis = seconds * 1e3 + Math.round(nanos / 1e6);
        return new Date(millis);
      }
      case 12:
        throw new Error(`timestamp96 is not supported yet`);
      default:
        throw new Error(`invalid byteLength ${byteLength}`);
    }
  }
};

// src/decoder.bs.js
var decode2 = /* @__PURE__ */ __name(function(_t, bytes) {
  let offset = 0;
  const end = bytes.length;
  const units = [];
  let result = "";
  while (offset < end) {
    const byte1 = bytes[offset++];
    if ((byte1 & 128) === 0) {
      units.push(byte1);
    } else if ((byte1 & 224) === 192) {
      const byte2 = bytes[offset++] & 63;
      units.push((byte1 & 31) << 6 | byte2);
    } else if ((byte1 & 240) === 224) {
      const byte2 = bytes[offset++] & 63;
      const byte3 = bytes[offset++] & 63;
      units.push((byte1 & 31) << 12 | byte2 << 6 | byte3);
    } else if ((byte1 & 248) === 240) {
      const byte2 = bytes[offset++] & 63;
      const byte3 = bytes[offset++] & 63;
      const byte4 = bytes[offset++] & 63;
      let unit = (byte1 & 7) << 18 | byte2 << 12 | byte3 << 6 | byte4;
      if (unit > 65535) {
        unit -= 65536;
        units.push(unit >>> 10 & 1023 | 55296);
        unit = 56320 | unit & 1023;
      }
      units.push(unit);
    } else {
      units.push(byte1);
    }
    if (units.length >= 4096) {
      result += String.fromCharCode(...units);
      units.length = 0;
    }
  }
  if (units.length > 0) {
    result += String.fromCharCode(...units);
  }
  return result;
}, "decode2");
var flip64 = /* @__PURE__ */ __name(function(binary) {
  let carry = 1;
  for (let i = 7; i >= 0; i--) {
    const v = (binary[i] ^ 255) + carry;
    binary[i] = v & 255;
    carry = v >> 8;
  }
}, "flip64");
function make(extensions) {
  var textDecoder = new TextDecoder();
  var extensions$1 = fromArray(extensions);
  return {
    textDecoder,
    extensions: extensions$1
  };
}
__name(make, "make");
function decode(t, binary) {
  var extensions = t.extensions;
  var textDecoder = t.textDecoder;
  var binary$1 = binary.slice();
  var view = new DataView(binary$1.buffer);
  var decode$1 = /* @__PURE__ */ __name(function(binary2, _state, _cursor) {
    while (true) {
      var cursor = _cursor;
      var state = _state;
      if (typeof state === "number") {
        var header = view.getUint8(cursor);
        var cursor$1 = cursor + 1 | 0;
        if (header < 128) {
          _cursor = cursor$1;
          _state = {
            TAG: (
              /* Done */
              5
            ),
            _0: header
          };
          continue;
        }
        if (header < 144) {
          var len = header & 15;
          _cursor = cursor$1;
          _state = {
            TAG: (
              /* DecodeMap */
              2
            ),
            _0: len,
            _1: {}
          };
          continue;
        }
        if (header < 160) {
          var len$1 = header & 15;
          _cursor = cursor$1;
          _state = {
            TAG: (
              /* DecodeArray */
              1
            ),
            _0: len$1,
            _1: new Array(len$1)
          };
          continue;
        }
        if (header < 192) {
          var len$2 = header & 31;
          _cursor = cursor$1;
          _state = {
            TAG: (
              /* DecodeString */
              0
            ),
            _0: len$2
          };
          continue;
        }
        switch (header) {
          case 192:
            _cursor = cursor$1;
            _state = {
              TAG: (
                /* Done */
                5
              ),
              _0: null
            };
            continue;
          case 193:
            break;
          case 194:
            _cursor = cursor$1;
            _state = {
              TAG: (
                /* Done */
                5
              ),
              _0: false
            };
            continue;
          case 195:
            _cursor = cursor$1;
            _state = {
              TAG: (
                /* Done */
                5
              ),
              _0: true
            };
            continue;
          case 196:
            var len$3 = view.getUint8(cursor$1);
            _cursor = cursor$1 + 1 | 0;
            _state = {
              TAG: (
                /* DecodeBinary */
                3
              ),
              _0: len$3
            };
            continue;
          case 197:
            var len$4 = view.getUint16(cursor$1);
            _cursor = cursor$1 + 2 | 0;
            _state = {
              TAG: (
                /* DecodeBinary */
                3
              ),
              _0: len$4
            };
            continue;
          case 198:
            var len$5 = view.getUint32(cursor$1);
            _cursor = cursor$1 + 4 | 0;
            _state = {
              TAG: (
                /* DecodeBinary */
                3
              ),
              _0: len$5
            };
            continue;
          case 199:
            var len$6 = view.getUint8(cursor$1);
            var type_ = view.getInt8(cursor$1 + 1 | 0);
            if (type_ === timestampDecoder.type) {
              _cursor = cursor$1 + 2 | 0;
              _state = {
                TAG: (
                  /* DecodeExt */
                  4
                ),
                _0: len$6,
                _1: timestampDecoder
              };
              continue;
            }
            var ext = get(extensions, String(type_));
            if (ext === void 0) {
              return raiseError("Unknown extension type " + String(type_));
            }
            _cursor = cursor$1 + 2 | 0;
            _state = {
              TAG: (
                /* DecodeExt */
                4
              ),
              _0: len$6,
              _1: ext
            };
            continue;
          case 200:
            var len$7 = view.getUint16(cursor$1);
            var type_$1 = view.getInt8(cursor$1 + 2 | 0);
            var ext$1 = get(extensions, String(type_$1));
            if (ext$1 === void 0) {
              return raiseError("Unknown extension type " + String(type_$1));
            }
            _cursor = cursor$1 + 2 | 0;
            _state = {
              TAG: (
                /* DecodeExt */
                4
              ),
              _0: len$7,
              _1: ext$1
            };
            continue;
          case 201:
            var len$8 = view.getUint32(cursor$1);
            var type_$2 = view.getInt8(cursor$1 + 4 | 0);
            var ext$2 = get(extensions, String(type_$2));
            if (ext$2 === void 0) {
              return raiseError("Unknown extension type " + String(type_$2));
            }
            _cursor = cursor$1 + 4 | 0;
            _state = {
              TAG: (
                /* DecodeExt */
                4
              ),
              _0: len$8,
              _1: ext$2
            };
            continue;
          case 202:
            var num = view.getFloat32(cursor$1);
            _cursor = cursor$1 + 4 | 0;
            _state = {
              TAG: (
                /* Done */
                5
              ),
              _0: num
            };
            continue;
          case 203:
            var num$1 = view.getFloat64(cursor$1);
            _cursor = cursor$1 + 8 | 0;
            _state = {
              TAG: (
                /* Done */
                5
              ),
              _0: num$1
            };
            continue;
          case 204:
            var num$2 = view.getUint8(cursor$1);
            _cursor = cursor$1 + 1 | 0;
            _state = {
              TAG: (
                /* Done */
                5
              ),
              _0: num$2
            };
            continue;
          case 205:
            var num$3 = view.getUint16(cursor$1);
            _cursor = cursor$1 + 2 | 0;
            _state = {
              TAG: (
                /* Done */
                5
              ),
              _0: num$3
            };
            continue;
          case 206:
            var num$4 = view.getUint32(cursor$1);
            _cursor = cursor$1 + 4 | 0;
            _state = {
              TAG: (
                /* Done */
                5
              ),
              _0: num$4
            };
            continue;
          case 207:
            var hi = view.getUint32(cursor$1);
            var lo = view.getUint32(cursor$1 + 4 | 0);
            var num$5 = hi * Math.pow(256, 4) + lo;
            _cursor = cursor$1 + 8 | 0;
            _state = {
              TAG: (
                /* Done */
                5
              ),
              _0: num$5
            };
            continue;
          case 208:
            var num$6 = view.getInt8(cursor$1);
            _cursor = cursor$1 + 1 | 0;
            _state = {
              TAG: (
                /* Done */
                5
              ),
              _0: num$6
            };
            continue;
          case 209:
            var num$7 = view.getInt16(cursor$1);
            _cursor = cursor$1 + 2 | 0;
            _state = {
              TAG: (
                /* Done */
                5
              ),
              _0: num$7
            };
            continue;
          case 210:
            var num$8 = view.getInt32(cursor$1);
            _cursor = cursor$1 + 4 | 0;
            _state = {
              TAG: (
                /* Done */
                5
              ),
              _0: num$8
            };
            continue;
          case 211:
            flip64(binary2.subarray(cursor$1, cursor$1 + 9 | 0));
            var hi$1 = view.getUint32(cursor$1);
            var lo$1 = view.getUint32(cursor$1 + 4 | 0);
            var num$9 = hi$1 * Math.pow(256, 4) + lo$1;
            _cursor = cursor$1 + 8 | 0;
            _state = {
              TAG: (
                /* Done */
                5
              ),
              _0: 0 - num$9
            };
            continue;
          case 212:
            var type_$3 = view.getInt8(cursor$1);
            var ext$3 = get(extensions, String(type_$3));
            if (ext$3 === void 0) {
              return raiseError("Unknown extension type " + String(type_$3));
            }
            _cursor = cursor$1 + 1 | 0;
            _state = {
              TAG: (
                /* DecodeExt */
                4
              ),
              _0: 1,
              _1: ext$3
            };
            continue;
          case 213:
            var type_$4 = view.getInt8(cursor$1);
            var ext$4 = get(extensions, String(type_$4));
            if (ext$4 === void 0) {
              return raiseError("Unknown extension type " + String(type_$4));
            }
            _cursor = cursor$1 + 1 | 0;
            _state = {
              TAG: (
                /* DecodeExt */
                4
              ),
              _0: 2,
              _1: ext$4
            };
            continue;
          case 214:
            var type_$5 = view.getInt8(cursor$1);
            if (type_$5 === timestampDecoder.type) {
              _cursor = cursor$1 + 1 | 0;
              _state = {
                TAG: (
                  /* DecodeExt */
                  4
                ),
                _0: 4,
                _1: timestampDecoder
              };
              continue;
            }
            var ext$5 = get(extensions, String(type_$5));
            if (ext$5 === void 0) {
              return raiseError("Unknown extension type " + String(type_$5));
            }
            _cursor = cursor$1 + 1 | 0;
            _state = {
              TAG: (
                /* DecodeExt */
                4
              ),
              _0: 4,
              _1: ext$5
            };
            continue;
          case 215:
            var type_$6 = view.getInt8(cursor$1);
            if (type_$6 === timestampDecoder.type) {
              _cursor = cursor$1 + 1 | 0;
              _state = {
                TAG: (
                  /* DecodeExt */
                  4
                ),
                _0: 8,
                _1: timestampDecoder
              };
              continue;
            }
            var ext$6 = get(extensions, String(type_$6));
            if (ext$6 === void 0) {
              return raiseError("Unknown extension type " + String(type_$6));
            }
            _cursor = cursor$1 + 1 | 0;
            _state = {
              TAG: (
                /* DecodeExt */
                4
              ),
              _0: 8,
              _1: ext$6
            };
            continue;
          case 216:
            var type_$7 = view.getInt8(cursor$1);
            var ext$7 = get(extensions, String(type_$7));
            if (ext$7 === void 0) {
              return raiseError("Unknown extension type " + String(type_$7));
            }
            _cursor = cursor$1 + 1 | 0;
            _state = {
              TAG: (
                /* DecodeExt */
                4
              ),
              _0: 16,
              _1: ext$7
            };
            continue;
          case 217:
            var len$9 = view.getUint8(cursor$1);
            _cursor = cursor$1 + 1 | 0;
            _state = {
              TAG: (
                /* DecodeString */
                0
              ),
              _0: len$9
            };
            continue;
          case 218:
            var len$10 = view.getUint16(cursor$1);
            _cursor = cursor$1 + 2 | 0;
            _state = {
              TAG: (
                /* DecodeString */
                0
              ),
              _0: len$10
            };
            continue;
          case 219:
            var len$11 = view.getUint32(cursor$1);
            _cursor = cursor$1 + 4 | 0;
            _state = {
              TAG: (
                /* DecodeString */
                0
              ),
              _0: len$11
            };
            continue;
          case 220:
            var len$12 = view.getUint16(cursor$1);
            _cursor = cursor$1 + 2 | 0;
            _state = {
              TAG: (
                /* DecodeArray */
                1
              ),
              _0: len$12,
              _1: new Array(len$12)
            };
            continue;
          case 221:
            var len$13 = view.getUint32(cursor$1);
            _cursor = cursor$1 + 4 | 0;
            _state = {
              TAG: (
                /* DecodeArray */
                1
              ),
              _0: len$13,
              _1: new Array(len$13)
            };
            continue;
          case 222:
            var len$14 = view.getUint16(cursor$1);
            _cursor = cursor$1 + 2 | 0;
            _state = {
              TAG: (
                /* DecodeMap */
                2
              ),
              _0: len$14,
              _1: {}
            };
            continue;
          case 223:
            var len$15 = view.getUint32(cursor$1);
            _cursor = cursor$1 + 4 | 0;
            _state = {
              TAG: (
                /* DecodeMap */
                2
              ),
              _0: len$15,
              _1: {}
            };
            continue;
          default:
        }
        if (header >= 256) {
          return raiseError("Unknown header " + String(header));
        }
        var num$10 = lnot(header ^ 255);
        _cursor = cursor$1;
        _state = {
          TAG: (
            /* Done */
            5
          ),
          _0: num$10
        };
        continue;
      }
      switch (state.TAG | 0) {
        case /* DecodeString */
        0:
          var len$16 = state._0;
          var view$1 = binary2.subarray(cursor, cursor + len$16 | 0);
          var text = decode2(textDecoder, view$1);
          _cursor = cursor + len$16 | 0;
          _state = {
            TAG: (
              /* Done */
              5
            ),
            _0: text
          };
          continue;
        case /* DecodeArray */
        1:
          var array = state._1;
          var len$17 = state._0;
          if (len$17 !== 0) {
            var match2 = decode$1(
              binary2,
              /* ExpectHeader */
              0,
              cursor
            );
            var index = array.length - len$17 | 0;
            array[index] = match2[0];
            _cursor = match2[1];
            _state = {
              TAG: (
                /* DecodeArray */
                1
              ),
              _0: len$17 - 1 | 0,
              _1: array
            };
            continue;
          }
          _state = {
            TAG: (
              /* Done */
              5
            ),
            _0: array
          };
          continue;
        case /* DecodeMap */
        2:
          var map = state._1;
          var len$18 = state._0;
          if (len$18 !== 0) {
            var match$1 = decode$1(
              binary2,
              /* ExpectHeader */
              0,
              cursor
            );
            var key = match$1[0];
            if (typeof key !== "string") {
              return raiseError("Unexpected key type. Expected string, but got " + typeof key);
            }
            var match$2 = decode$1(
              binary2,
              /* ExpectHeader */
              0,
              match$1[1]
            );
            map[key] = match$2[0];
            _cursor = match$2[1];
            _state = {
              TAG: (
                /* DecodeMap */
                2
              ),
              _0: len$18 - 1 | 0,
              _1: map
            };
            continue;
          }
          _state = {
            TAG: (
              /* Done */
              5
            ),
            _0: map
          };
          continue;
        case /* DecodeBinary */
        3:
          var len$19 = state._0;
          var copy = binary2.slice(cursor, cursor + len$19 | 0);
          _cursor = cursor + len$19 | 0;
          _state = {
            TAG: (
              /* Done */
              5
            ),
            _0: copy
          };
          continue;
        case /* DecodeExt */
        4:
          var len$20 = state._0;
          var copy$1 = binary2.slice(cursor, cursor + len$20 | 0);
          _cursor = cursor + len$20 | 0;
          _state = {
            TAG: (
              /* Done */
              5
            ),
            _0: state._1.decode(copy$1, len$20)
          };
          continue;
        case /* Done */
        5:
          return [
            state._0,
            cursor
          ];
      }
    }
    ;
  }, "decode$1");
  var match = decode$1(
    binary$1,
    /* ExpectHeader */
    0,
    0
  );
  var readLength = match[1];
  var inputLength = binary$1.length;
  if (inputLength !== readLength) {
    raiseError("Invalid input length, expected " + String(inputLength) + ", but got " + String(readLength));
  }
  return match[0];
}
__name(decode, "decode");

// src/decoder.ts
function makeMessagePackDecoder({
  decoderExtensions = []
} = {}) {
  let decoder = make(decoderExtensions);
  return {
    decode: /* @__PURE__ */ __name((input) => decode(decoder, input), "decode")
  };
}
__name(makeMessagePackDecoder, "makeMessagePackDecoder");
export {
  makeMessagePackDecoder,
  makeMessagePackEncoder
};
/*! For license information please see index.mjs.LEGAL.txt */
//# sourceMappingURL=index.mjs.map
